import { authApi, PaymentOrderResponse } from './authApi.ts';

export interface RazorpayResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

export interface RazorpayErrorResponse {
  error: {
    code: string;
    description: string;
    source: string;
    step: string;
    reason: string;
    metadata: {
      order_id: string;
      payment_id: string;
    };
  };
}

export interface RazorpayOptions {
  key: string;
  amount: string;
  currency: string;
  name: string;
  description: string;
  order_id: string;
  prefill: {
    name: string;
    email: string;
    contact: string;
  };
  theme: {
    color: string;
  };
  handler: (response: RazorpayResponse) => void;
  modal: {
    ondismiss: () => void;
  };
}

declare global {
  interface Window {
    Razorpay: any;
  }
}

/**
 * Payment Service
 *
 * Handles Razorpay integration and payment flow
 */
export const paymentService = {
  /**
   * Initialize payment checkout
   *
   * @param planId - Plan ID to subscribe to
   * @param onSuccess - Callback on successful payment
   * @param onFailure - Callback on payment failure
   */
  async initiatePayment(
    planId: number,
    onSuccess: (response: RazorpayResponse) => void,
    onFailure: (error: RazorpayErrorResponse) => void
  ): Promise<void> {
    try {
      // Step 1: Create order on backend
      const orderResponse = await authApi.createPaymentOrder({ planId });
      const orderData: PaymentOrderResponse = orderResponse.data;

      // Step 2: Configure Razorpay options
      const options: RazorpayOptions = {
        key: orderData.key,
        amount: orderData.amount.toString(),
        currency: orderData.currency,
        name: 'Swing Screener Pro',
        description: 'Premium Subscription',
        order_id: orderData.orderId,
        prefill: {
          name: orderData.userName,
          email: orderData.userEmail,
          contact: orderData.userContact,
        },
        theme: {
          color: '#3399cc',
        },
        handler: (response: RazorpayResponse) => {
          // Payment successful
          onSuccess(response);
        },
        modal: {
          ondismiss: () => {
            console.log('Payment modal closed by user');
          },
        },
      };

      // Step 3: Open Razorpay checkout
      const razorpayInstance = new window.Razorpay(options);

      razorpayInstance.on('payment.failed', (response: RazorpayErrorResponse) => {
        // Payment failed
        onFailure(response);
      });

      razorpayInstance.open();
    } catch (error: any) {
      console.error('Error initiating payment:', error);

      // Convert error to RazorpayErrorResponse format
      const errorResponse: RazorpayErrorResponse = {
        error: {
          code: error.response?.data?.status || 'UNKNOWN_ERROR',
          description: error.response?.data?.message || 'Failed to initiate payment',
          source: 'backend',
          step: 'order_creation',
          reason: error.message,
          metadata: {
            order_id: '',
            payment_id: '',
          },
        },
      };

      onFailure(errorResponse);
    }
  },

  /**
   * Verify payment on backend
   *
   * @param response - Razorpay payment response
   * @returns Verification response
   */
  async verifyPayment(response: RazorpayResponse) {
    try {
      const verificationResponse = await authApi.verifyPayment({
        razorpayOrderId: response.razorpay_order_id,
        razorpayPaymentId: response.razorpay_payment_id,
        razorpaySignature: response.razorpay_signature,
      });

      return verificationResponse;
    } catch (error: any) {
      console.error('Error verifying payment:', error);
      throw error;
    }
  },
};

export default paymentService;
